import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import { handleAuthError, formatErrorMessage } from '../utils/errorHandler';

interface User {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  isAdmin?: boolean;
  isBetaUser?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshToken: () => Promise<boolean>;
  error: string | null;
  successMessage: string | null;
  clearError: () => void;
  clearSuccessMessage: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const userData = localStorage.getItem('portal_user');
      
      if (token && userData) {
        try {
          // Restore user from localStorage first
          const storedUser = JSON.parse(userData);
          setUser(storedUser);
          
          // Then validate token with backend
          const freshUserData = await apiService.getCurrentUser();
          setUser(freshUserData);
          localStorage.setItem('portal_user', JSON.stringify(freshUserData));
        } catch (error) {
          // Clear invalid data
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_user');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      localStorage.setItem('portal_user', JSON.stringify(userData));
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    try {
      const response = await apiService.login(email, password);
      localStorage.setItem('portal_token', response.token);
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (err: any) {
      const errorState = handleAuthError(err);
      const formattedError = formatErrorMessage(errorState);
      setError(formattedError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    setUser(null);
    window.location.href = '/portal';
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    setError(null);
    setLoading(true);
    try {
      const response = await apiService.register(email, password, displayName);
      localStorage.setItem('portal_token', response.token);
      localStorage.setItem('portal_user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (err: any) {
      const errorState = handleAuthError(err);
      setError(formatErrorMessage(errorState));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    try {
      await apiService.forgotPassword(email);
      const msg = 'Password reset email sent successfully. Please check your inbox.';
      setSuccessMessage(msg);
    } catch (err: any) {
      const errorState = handleAuthError(err);
      setError(formatErrorMessage(errorState));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccessMessage = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, signUp, forgotPassword, logout, loading, refreshToken, error, successMessage, clearError, clearSuccessMessage }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
